// packages/api/src/hooks/useAdminRegistrationsLive.ts

/**
 * Phase 01 W3.10 — Live updates for the admin registration queue.
 *
 * Backend (api-alamo W2.1 commit dc3dec0) emits `registration.status.changed`
 * to the `admin:registrations` Socket.IO room every time a registration row
 * transitions state. This hook opens an authenticated socket connection, joins
 * the room automatically (server-side: admins/gerentes auto-join on connect),
 * and invalidates the admin queue + stats query keys so the UI reflects the
 * fresh data without manual reload.
 *
 * The hook is a no-op when there is no access token (logged out / pre-auth).
 */

import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';

import { getAccessToken } from '../client';
import { queryKeys } from '../query-keys';

import { resolveSocketUrl } from './utils/socket-shared';

const SOCKET_URL = resolveSocketUrl();

export function useAdminRegistrationsLive(options?: { enabled?: boolean }): void {
  const enabled = options?.enabled ?? true;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    const token = getAccessToken();
    if (!token) return;

    let socket: Socket | null = null;
    try {
      socket = io(SOCKET_URL, {
        transports: ['websocket'],
        auth: { token },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1500,
      });

      socket.on('registration.status.changed', () => {
        // Refetch queue list + stats. We intentionally invalidate the whole
        // `consultantApproval` namespace so any active filter snapshot picks
        // up the row that moved into/out of its bucket.
        void queryClient.invalidateQueries({ queryKey: queryKeys.consultantApproval.all });
      });

      socket.on('connect_error', () => {
        // Silent: connection failures during reconnect are expected when the
        // backend is restarting or the user's JWT is mid-rotation. The next
        // poll cycle (TanStack Query default refetchOnWindowFocus) will fill
        // any gap.
      });
    } catch {
      // Defensive — socket.io-client is permissive but throwing here would
      // crash the admin queue page on first paint. Swallow and rely on the
      // normal polling fallback.
    }

    return () => {
      socket?.disconnect();
    };
  }, [enabled, queryClient]);
}
